import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { db } from "../firebase/firebaseConfig";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { clearCart } from "../redux/cartSlice";

/* ─── Design tokens (mirror Footer.css / Navbar.css) ─────── */
const T = {
  rose:       "#c0506e",
  roseDark:   "#8f2d46",
  roseLight:  "#e8839a",
  roseSoft:   "#fdf0f3",
  roseMid:    "#f3c5cf",
  ivory:      "#faf8f5",
  ivoryDark:  "#f0ece6",
  warm100:    "#e8e3dc",
  warm200:    "#d4cfc8",
  warm400:    "#9e9189",
  warm700:    "#3d3530",
  ink:        "#1a1510",
  ffDisplay:  "'Cormorant Garamond', Georgia, serif",
  ffBody:     "'Outfit', system-ui, sans-serif",
  easeLux:    "cubic-bezier(0.16, 1, 0.3, 1)",
};

/* ─── Validation helpers ──────────────────────────────────── */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidPhone = (v) => /^[0-9]{10}$/.test(v);

/* ─── Sub-components ──────────────────────────────────────── */

/** Decorative rose accent bar */
const AccentBar = () => (
  <div style={{
    height: 3,
    background: `linear-gradient(90deg,
      transparent 0%, ${T.roseMid} 15%,
      ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)`,
    borderRadius: "0 0 2px 2px",
  }} />
);

/** Card wrapper — replaces MUI Paper */
const Card = ({ children, style }) => (
  <div style={{
    background: "#fff",
    borderRadius: 16,
    border: `1px solid ${T.warm100}`,
    boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.06)`,
    overflow: "hidden",
    ...style,
  }}>
    <AccentBar />
    <div style={{ padding: "clamp(1.5rem, 4vw, 2.25rem)" }}>
      {children}
    </div>
  </div>
);

/** Section heading */
const SectionTitle = ({ children }) => (
  <h2 style={{
    fontFamily: T.ffDisplay,
    fontSize: "clamp(1.25rem, 3vw, 1.55rem)",
    fontWeight: 700,
    color: T.roseDark,
    letterSpacing: "-0.01em",
    margin: "0 0 .15rem",
  }}>{children}</h2>
);

/** Thin divider */
const Hr = ({ style }) => (
  <hr style={{
    border: "none",
    borderTop: `1px solid ${T.warm100}`,
    margin: "1.25rem 0",
    ...style,
  }} />
);

/** Labelled text field */
const Field = ({ label, hint, error, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: "1.1rem" }}>
      <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? T.rose : focused ? T.rose : T.warm400,
        marginBottom: 6,
        transition: "color 180ms",
        fontFamily: T.ffBody,
      }}>{label}</label>
      <input
        {...props}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "0.75rem 1rem",
          fontFamily: T.ffBody,
          fontSize: 14,
          color: T.ink,
          background: focused ? "#fff" : T.ivory,
          border: `1.5px solid ${error ? T.rose : focused ? T.rose : T.warm200}`,
          borderRadius: 10,
          outline: "none",
          transition: `border-color 200ms, background 200ms, box-shadow 200ms`,
          boxShadow: focused
            ? `0 0 0 3px rgba(192,80,110,.12)`
            : "none",
        }}
      />
      {(hint || error) && (
        <p style={{
          margin: "5px 0 0 2px",
          fontSize: 12,
          color: error ? T.rose : T.warm400,
          fontFamily: T.ffBody,
        }}>{error || hint}</p>
      )}
    </div>
  );
};

/** Cart line item */
const LineItem = ({ item }) => (
  <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "1rem",
    padding: "0.6rem 0",
    borderBottom: `1px solid ${T.warm100}`,
  }}>
    <span style={{
      fontFamily: T.ffBody,
      fontSize: 14,
      color: T.warm700,
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}>
      {item.name}
      <span style={{
        marginLeft: 6,
        fontSize: 12,
        color: T.warm400,
        fontWeight: 500,
      }}>×{item.quantity}</span>
    </span>
    <span style={{
      fontFamily: T.ffDisplay,
      fontWeight: 700,
      fontSize: 15,
      color: T.roseDark,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}>
      GH₵{(item.price * item.quantity).toFixed(2)}
    </span>
  </div>
);

/** Status alert */
const StatusAlert = ({ status, onClose }) => {
  if (!status) return null;
  const isError = status.type === "error";
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "1rem 1.25rem",
      borderRadius: 12,
      border: `1px solid ${isError ? "#f3c5cf" : "#b7e3c8"}`,
      background: isError ? T.roseSoft : "#f0faf5",
      marginBottom: "1.75rem",
      animation: "fadeSlideIn 300ms cubic-bezier(0.16,1,0.3,1) both",
    }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
        {isError ? "⚠️" : "✓"}
      </span>
      <p style={{
        margin: 0,
        fontSize: 14,
        fontFamily: T.ffBody,
        color: isError ? T.roseDark : "#1a5c35",
        flex: 1,
        lineHeight: 1.5,
      }}>{status.message}</p>
      <button
        onClick={onClose}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: T.warm400,
          fontSize: 16,
          padding: 0,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >✕</button>
    </div>
  );
};

/* ─── Main component ──────────────────────────────────────── */
const CheckoutPage = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);

  const [info, setInfo] = useState({ name: "", email: "", address: "", phone: "" });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  /* Field-level errors (only after blur) */
  const errors = {
    name:  touched.name  && !info.name          ? "Full name is required" : null,
    email: touched.email && !isValidEmail(info.email) ? "Enter a valid email address" : null,
    phone: touched.phone && !isValidPhone(info.phone) ? "Enter a valid 10-digit number" : null,
  };

  const setField = useCallback((key, val) =>
    setInfo((prev) => ({ ...prev, [key]: val })), []);

  const blur = useCallback((key) =>
    setTouched((prev) => ({ ...prev, [key]: true })), []);

  const getTotalPrice = () =>
    cartItems.reduce((acc, item) => {
      const t = parseFloat(item.price) * parseInt(item.quantity);
      return acc + (isNaN(t) ? 0 : t);
    }, 0);

  const totalPrice = getTotalPrice();

  const canCheckout =
    info.name &&
    isValidEmail(info.email) &&
    isValidPhone(info.phone) &&
    cartItems.length > 0 &&
    !loading;

  const handleCheckout = async () => {
    /* Mark all fields touched to surface errors */
    setTouched({ name: true, email: true, phone: true });

    if (!info.name || !isValidEmail(info.email) || !isValidPhone(info.phone)) {
      setStatus({ type: "error", message: "Please fill all fields correctly." });
      return;
    }

    try {
      setLoading(true);
      await addDoc(collection(db, "orders"), {
        ...info,
        cartItems,
        totalAmount: Number(totalPrice),
        status: "Pending",
        createdAt: Timestamp.now(),
        timestamp: Timestamp.now(),
      });

      dispatch(clearCart());
      setStatus({ type: "success", message: "Order placed successfully! We'll be in touch soon." });
      setInfo({ name: "", email: "", address: "", phone: "" });
      setTouched({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Firestore Error:", err);
      setStatus({ type: "error", message: "Failed to place order. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  /* ── Page shell ─────────────────────────────────────────── */
  return (
    <>
      {/* Keyframes injected once */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');

        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0);    }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .checkout-btn {
          transition: background 220ms, box-shadow 220ms, transform 200ms;
        }
        .checkout-btn:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .checkout-btn:not(:disabled):active {
          transform: translateY(0);
        }
        .checkout-btn:disabled {
          opacity: .55;
          cursor: not-allowed;
        }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse 70% 50% at 100% 0%, rgba(192,80,110,.06) 0%, transparent 65%),
          radial-gradient(ellipse 50% 40% at 0% 100%, rgba(192,80,110,.04) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        fontFamily: T.ffBody,
        padding: "clamp(2rem, 6vw, 4.5rem) clamp(1rem, 5vw, 2rem)",
      }}>

        {/* ── Page title ── */}
        <div style={{
          maxWidth: 1100,
          margin: "0 auto clamp(1.75rem, 4vw, 2.5rem)",
        }}>
          <p style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: T.rose,
            margin: "0 0 .4rem",
            fontFamily: T.ffBody,
          }}>GlossyLuxHair</p>
          <h1 style={{
            fontFamily: T.ffDisplay,
            fontWeight: 700,
            fontSize: "clamp(2rem, 5vw, 2.75rem)",
            color: T.ink,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            margin: 0,
          }}>
            Checkout
          </h1>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          <StatusAlert status={status} onClose={() => setStatus(null)} />

          {cartItems.length === 0 ? (
            /* ── Empty state ── */
            <Card>
              <div style={{ textAlign: "center", padding: "2rem 0" }}>
                <div style={{
                  fontSize: 40,
                  marginBottom: "1rem",
                  filter: "grayscale(.4)",
                }}>🛒</div>
                <p style={{
                  fontFamily: T.ffDisplay,
                  fontSize: "1.2rem",
                  color: T.warm700,
                  margin: 0,
                }}>Your cart is empty</p>
                <p style={{
                  fontSize: 14,
                  color: T.warm400,
                  marginTop: ".5rem",
                }}>Add some products and come back to checkout.</p>
              </div>
            </Card>
          ) : (
            /* ── Two-column layout ── */
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
              gap: "clamp(1.25rem, 3vw, 2rem)",
              alignItems: "start",
            }}>

              {/* ORDER SUMMARY */}
              <Card>
                <SectionTitle>Order Summary</SectionTitle>
                <p style={{
                  fontSize: 13,
                  color: T.warm400,
                  margin: "0 0 1rem",
                  fontFamily: T.ffBody,
                }}>
                  {cartItems.length} item{cartItems.length !== 1 ? "s" : ""}
                </p>

                <div>
                  {cartItems.map((item) => (
                    <LineItem key={item.id} item={item} />
                  ))}
                </div>

                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginTop: "1.25rem",
                  padding: "1rem 1.1rem",
                  background: T.roseSoft,
                  borderRadius: 10,
                  border: `1px solid ${T.roseMid}`,
                }}>
                  <span style={{
                    fontFamily: T.ffBody,
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: T.roseDark,
                  }}>Total</span>
                  <span style={{
                    fontFamily: T.ffDisplay,
                    fontWeight: 700,
                    fontSize: "clamp(1.35rem, 3vw, 1.6rem)",
                    color: T.roseDark,
                  }}>
                    GH₵{totalPrice.toFixed(2)}
                  </span>
                </div>
              </Card>

              {/* BILLING FORM */}
              <Card>
                <SectionTitle>Billing Information</SectionTitle>
                <p style={{
                  fontSize: 13,
                  color: T.warm400,
                  margin: "0 0 1.5rem",
                  fontFamily: T.ffBody,
                }}>
                  All fields marked are required
                </p>

                <Field
                  label="Full Name *"
                  type="text"
                  placeholder="Jane Doe"
                  value={info.name}
                  error={errors.name}
                  onChange={(e) => setField("name", e.target.value)}
                  onBlur={() => blur("name")}
                  autoComplete="name"
                />

                <Field
                  label="Email Address *"
                  type="email"
                  placeholder="jane@example.com"
                  value={info.email}
                  error={errors.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onBlur={() => blur("email")}
                  autoComplete="email"
                />

                <Field
                  label="Shipping Address"
                  type="text"
                  placeholder="123 Rose Street, Accra"
                  value={info.address}
                  onChange={(e) => setField("address", e.target.value)}
                  autoComplete="street-address"
                />

                <Field
                  label="Phone Number *"
                  type="tel"
                  placeholder="0201234567"
                  value={info.phone}
                  error={errors.phone}
                  hint={!errors.phone ? "10-digit Ghana number" : null}
                  maxLength={10}
                  onChange={(e) => setField("phone", e.target.value.replace(/\D/g, ""))}
                  onBlur={() => blur("phone")}
                  autoComplete="tel"
                  inputMode="numeric"
                />

                <Hr />

                {/* Secure badge */}
                <p style={{
                  fontSize: 12,
                  color: T.warm400,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  margin: "0 0 1rem",
                }}>
                  <span style={{ fontSize: 14 }}>🔒</span>
                  Your information is safe and encrypted
                </p>

                <button
                  className="checkout-btn"
                  disabled={!canCheckout}
                  onClick={handleCheckout}
                  style={{
                    width: "100%",
                    padding: "1rem 1.5rem",
                    fontFamily: T.ffBody,
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: "0.04em",
                    color: "#fff",
                    background: T.rose,
                    border: "none",
                    borderRadius: 12,
                    cursor: canCheckout ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    boxShadow: `0 4px 20px rgba(192,80,110,.22)`,
                  }}
                >
                  {loading ? (
                    <>
                      <span style={{
                        width: 16,
                        height: 16,
                        border: "2px solid rgba(255,255,255,.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin .7s linear infinite",
                      }} />
                      Processing…
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 16 }}>✦</span>
                      Place Order · GH₵{totalPrice.toFixed(2)}
                    </>
                  )}
                </button>
              </Card>

            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default CheckoutPage;